/**
 * Inline boot-recovery script, injected into <head> by root.tsx.
 *
 * Why this exists: on 2026-08-04 the Cloudflare edge served 503 for freshly
 * deployed assets at staging-admin.palate.pro for a few minutes after a Pages
 * deploy — `/assets/root-*.css` returned 503 four times in the browser while
 * `curl` for the same URL returned 200. Because this app is SPA-mode, the
 * loading splash is baked into index.html, so a failed entry asset leaves the
 * user staring at that splash forever: no error, no retry, no console output.
 * It reads as a total outage and cost a needless revert.
 *
 * This cannot fix the 503 (that is edge-side). It fixes the app's reaction to
 * one: recover automatically when the failure is transient, and say something
 * honest when it is not.
 *
 * It must be INLINE. A module that has to be fetched cannot rescue a boot that
 * is failing because fetches are failing. It must also never throw — every
 * storage access is guarded, since Safari private mode throws on sessionStorage.
 *
 * NOTE: the script body below is a String.raw template — it must contain NO
 * backticks, or it terminates the literal and the build fails to transform.
 *
 * Four triggers, because they catch different failures:
 *   1. capture-phase `error` on <script>/<link> — the entry bundle or the
 *      stylesheet failing. This is what actually happened; `vite:preloadError`
 *      does NOT fire for these.
 *   2. `vite:preloadError` — a dynamic route chunk failing, the classic
 *      post-deploy version skew where index.html points at chunks that a new
 *      deploy replaced.
 *   3. shortly after `load`, `__reactRouterRouteModules` still undefined — a
 *      far better failure signal than "React hasn't mounted", because that
 *      global is exactly what a failed static import prevents. Recovers in
 *      ~2s instead of waiting out the 8s grace. (DOMContentLoaded is NOT safe
 *      for this: measured firing at 17ms, before the imports resolve.)
 *   4. a mount watchdog — backstop for anything that leaves us on the splash
 *      without surfacing an error at all.
 *
 * Why recovery must be a full page reload rather than retrying the import:
 * once a module fails, the browser caches that failure in the document's module
 * map for the lifetime of the document. Re-importing the same specifier returns
 * the cached failure and never re-fetches. Only a fresh navigation clears it.
 *
 * Recovery is a single guarded reload: a reload re-fetches index.html (served
 * `must-revalidate`, so never stale) and with it the current asset hashes. If
 * that still fails we stop and show a retry button rather than reload-looping.
 *
 * A failed asset does NOT mean a failed boot, so nothing happens immediately.
 * We wait a grace period and act only if React still hasn't mounted. Verified
 * locally by 404ing the stylesheet: the app mounts fine, just unstyled, and
 * reloading it would have been pure waste — worse, the failure screen would
 * have been painted and then immediately clobbered by React rendering.
 *
 * Bug-255 — AFTER the app is up (a tab left open across a deploy). Everything
 * above is about booting. A tab that has been open for hours holds the previous
 * build's chunk names; once a deploy replaces them the host answers those URLs
 * 200 text/html (SPA fallback) and the next route navigation's `import()` fails.
 *
 * Vite's runtime helper is `baseModule().catch(handlePreloadError)`, and
 * handlePreloadError rethrows ONLY if the `vite:preloadError` event was not
 * defaultPrevented. React Router loads every route module through that helper.
 * Calling preventDefault() therefore turned the failed route import into a
 * promise that resolves to `undefined`: React Router's own "reloading page"
 * never ran, its module cache was poisoned with `undefined`, and the user got a
 * blank page (verified in Chromium against the built app) with nothing on
 * screen to say why. And the grace-period recovery above stood down for good,
 * because `__palateMounted` is true from the first splash render.
 *
 * So once the app is up (`__palateReady`, set by __palateBootOk, which App calls):
 *   - the failure is NOT swallowed. React Router reloads once, as designed; a
 *     React.lazy boundary or an event handler gets a real rejection it can show
 *     instead of `undefined`.
 *   - the failure is remembered for CHUNK_LOOP_WINDOW_MS. If it comes straight
 *     back after a reload (the reload did not cure it), or a reload cannot be
 *     tracked (storage blocked) or would only land on the offline page, the
 *     event IS prevented — the one way to stop React Router's unguarded reload
 *     — and a full-screen "Reload" overlay says what is wrong.
 *   - `palate:stale-build` is announced either way, so the app can offer a
 *     Reload button for the callers React Router does not reload for.
 */
export const BOOT_RECOVERY_SCRIPT = String.raw`
(function () {
  // Idempotent: if this ever runs twice on one document, a single failed asset
  // would otherwise be handled by both copies and reload twice.
  if (window.__palateBootRecoveryInstalled) return;
  window.__palateBootRecoveryInstalled = true;

  var RETRY_KEY = "palate:boot-retry";
  var MOUNT_TIMEOUT_MS = 20000;
  // Generous on purpose. A failed asset is not proof of a failed boot, and a
  // cold load of this app is several seconds — measured locally, React mounted
  // ~5s in with a 404 stylesheet. Anything tighter races normal startup and
  // reloads a page that was about to work.
  var GRACE_MS = 8000;
  // How long after the load event to let React Router's bootstrap finish
  // assigning __reactRouterRouteModules before treating its absence as failure.
  var BOOTSTRAP_SETTLE_MS = 2000;
  // Bug-255: a chunk failure after the app is up. One failure inside this window
  // is "a deploy replaced the chunks": reload and it is cured. A second one
  // inside it means the reload did not help, and reloading again would loop.
  var CHUNK_KEY = "palate:chunk-failure-at";
  var CHUNK_LOOP_WINDOW_MS = 60000;
  var handled = false;
  // A vite:preloadError arrived before the app was up (see recoverIfDead).
  var preloadFailed = false;
  // Post-boot: this document already decided what a chunk failure means.
  var chunkFailureSeen = false;
  var chunkBlocked = false;

  // __palateMounted only says Layout rendered — and Layout renders the
  // HydrateFallback splash. __palateReady says App rendered, i.e. the route the
  // user asked for loaded. Only the latter means "the app is up".
  function appReady() { return window.__palateReady === true; }

  function readRetried() {
    try { return sessionStorage.getItem(RETRY_KEY) === "1"; } catch (e) { return false; }
  }
  function markRetried() {
    try { sessionStorage.setItem(RETRY_KEY, "1"); } catch (e) {}
  }

  // Appended as an overlay, never a body replacement. Overwriting document.body
  // deletes the module scripts React Router injected — locally that turned a
  // load that WOULD have mounted into one that never could. If the app comes up
  // afterwards, __palateBootOk removes this.
  function showOverlay(id, title, body, buttonId, buttonLabel, onClick) {
    try {
      if (document.getElementById(id)) return;
      var host = document.createElement("div");
      host.id = id;
      host.setAttribute("role", "alertdialog");
      host.setAttribute("aria-modal", "true");
      host.setAttribute("aria-labelledby", id + "-title");
      host.setAttribute("aria-describedby", id + "-body");
      host.setAttribute(
        "style",
        "position:fixed;inset:0;z-index:2147483647;background:#fff;overflow:auto"
      );
      host.innerHTML =
        '<div style="min-height:100vh;display:flex;align-items:center;justify-content:center;' +
        'font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif;padding:24px;text-align:center">' +
        '<div style="max-width:32rem">' +
        '<h1 id="' + id + '-title" style="font-size:1.125rem;font-weight:600;margin:0 0 8px">' + title + '</h1>' +
        '<p id="' + id + '-body" style="color:#555;font-size:0.875rem;line-height:1.5;margin:0 0 16px">' +
        body +
        '</p>' +
        '<button id="' + buttonId + '" style="background:#2f6b63;color:#fff;border:0;border-radius:6px;' +
        'padding:8px 16px;font-size:0.875rem;cursor:pointer">' + buttonLabel + '</button>' +
        '</div></div>';
      document.body.appendChild(host);
      var btn = document.getElementById(buttonId);
      if (btn) {
        btn.addEventListener("click", onClick);
        try { btn.focus(); } catch (e) {}
      }
    } catch (e) {}
  }

  function showFailure() {
    showOverlay(
      "palate-boot-failure",
      "Palate could not finish loading",
      "Some of the app files did not load. This is usually temporary and clears on its own within a few minutes.",
      "palate-boot-retry",
      "Try again",
      function () {
        try { sessionStorage.removeItem(RETRY_KEY); } catch (e) {}
        window.location.reload();
      }
    );
  }

  // Bug-255: the app was up and a piece of it would not load. Unlike
  // showFailure this is not "wait, it may clear": the page is genuinely broken
  // for what the user just tried, so say so and offer the one thing that works.
  function showStaleBuild() {
    showOverlay(
      "palate-stale-build",
      "Part of Palate could not load",
      "A newer version may have been released while this page was open, or the connection dropped. " +
        "Reload to continue. Anything you were editing on this page will be lost.",
      "palate-stale-reload",
      "Reload",
      function () {
        try { sessionStorage.removeItem(CHUNK_KEY); } catch (e) {}
        window.location.reload();
      }
    );
  }

  // Tells the app (root.tsx's new-version prompt) that this tab is probably on
  // an old build. A flag as well as an event: the listener may mount later.
  function announceStaleBuild() {
    try {
      window.__palateStaleBuild = true;
      window.dispatchEvent(new CustomEvent("palate:stale-build"));
    } catch (e) {}
  }

  // True when a chunk failure was recorded within the loop window — i.e. the
  // page that is failing now is the one a reload already produced. Unreadable
  // storage answers "yes": a reload that cannot be tracked cannot be stopped.
  function chunkReloadAlreadyUsed() {
    try {
      var at = Number(sessionStorage.getItem(CHUNK_KEY));
      var age = Date.now() - at;
      return at > 0 && age >= 0 && age < CHUNK_LOOP_WINDOW_MS;
    } catch (e) { return true; }
  }
  function recordChunkFailure() {
    try { sessionStorage.setItem(CHUNK_KEY, String(Date.now())); return true; } catch (e) { return false; }
  }

  // Whether a failed element is one of OUR build's files. Anything else (maps,
  // hCaptcha, Stripe.js) failing says nothing about a new deploy.
  function isOwnAsset(el) {
    try {
      var url = el.src || el.href || "";
      return url.indexOf(window.location.origin + "/assets/") === 0;
    } catch (e) { return false; }
  }

  // Acts only if React still hasn't mounted. A missing stylesheet, for
  // instance, leaves a perfectly usable (if unstyled) app — reloading it would
  // waste a round trip on every load, and repainting the body would fight
  // React for the DOM.
  function recoverIfDead() {
    if (handled) return;
    if (appReady()) return;
    // Mounted-but-not-ready is the splash. That is a dead boot only when a
    // route chunk is known to have failed (a stylesheet alone leaves a usable
    // app); otherwise it is just a slow load.
    if (window.__palateMounted && !preloadFailed) return;
    handled = true;
    if (readRetried()) { showFailure(); return; }
    markRetried();
    window.location.reload();
  }

  function scheduleRecovery() {
    if (handled) return;
    window.setTimeout(recoverIfDead, GRACE_MS);
  }

  // 1. Entry bundle or stylesheet failed to load. Resource errors do not
  //    bubble, so this has to listen in the capture phase.
  window.addEventListener(
    "error",
    function (event) {
      var el = event && event.target;
      if (!el || el === window) return;
      var tag = el.tagName;
      if (tag !== "SCRIPT" && tag !== "LINK") return;
      // App already up: nothing to recover. But one of our own files failing
      // (a modulepreload for a route the user is about to open, say) means this
      // tab's build has been replaced — say so while the user can still act.
      if (appReady()) {
        if (isOwnAsset(el) && navigator.onLine !== false) announceStaleBuild();
        return;
      }
      scheduleRecovery();
    },
    true
  );

  // 2. A dynamic route chunk failed.
  //
  //    While booting, preventDefault() stops Vite rethrowing and the guarded
  //    recovery below owns the reload — React Router's own reload is unguarded
  //    and would loop for as long as the host keeps failing.
  //
  //    Once the app is up it is the other way round (Bug-255, see the header):
  //    swallowing the failure resolves import() to undefined and leaves a blank
  //    page. Let it through, unless a reload already failed to cure it.
  window.addEventListener("vite:preloadError", function (event) {
    if (!appReady()) {
      preloadFailed = true;
      if (event && event.preventDefault) event.preventDefault();
      scheduleRecovery();
      return;
    }

    announceStaleBuild();

    if (chunkBlocked) {
      // The Reload overlay is up; nothing further may navigate or reload.
      if (event && event.preventDefault) event.preventDefault();
      return;
    }
    if (chunkFailureSeen) return; // one navigation can fail several chunks
    chunkFailureSeen = true;

    var canReload =
      navigator.onLine !== false && !chunkReloadAlreadyUsed() && recordChunkFailure();
    if (!canReload) {
      chunkBlocked = true;
      if (event && event.preventDefault) event.preventDefault();
      showStaleBuild();
    }
    // else: leave the event alone. Vite rethrows to the caller; React Router's
    // route loader reloads the page, and the reload fetches the current build.
  });

  // 3. The precise signal. React Router's SPA bootstrap is an inline
  //    <script type="module"> doing STATIC imports:
  //
  //      import "/assets/manifest-*.js";
  //      import * as route0 from "/assets/root-*.js";
  //      window.__reactRouterRouteModules = { root: route0 };
  //      import("/assets/entry.client-*.js");
  //
  //    A failed *static* import aborts that whole module silently — the assign
  //    never runs, hydration never starts, and nothing is logged. So the global
  //    being absent is a far better failure signal than "React hasn't mounted",
  //    and it lets us recover in ~2s instead of waiting out GRACE_MS.
  //
  //    Measured, not assumed: DOMContentLoaded is NOT safe here. It fired at
  //    17ms on a healthy load — well before the module's static imports had
  //    resolved — so checking there reloaded a perfectly good page every time.
  //    The load event waits for those imports, and BOOTSTRAP_SETTLE_MS gives
  //    the assignment room to land after it.
  //
  //    Two layers of protection against a false positive: the global must be
  //    absent, AND recoverIfDead() independently bails if React has mounted.
  //    A false reload therefore needs both to be wrong at once.
  //
  //    Deliberately additive: if React Router ever renames this global, this
  //    check simply never fires and the timing-based triggers still cover us.
  window.addEventListener("load", function () {
    window.setTimeout(function () {
      if (typeof window.__reactRouterRouteModules === "undefined") {
        recoverIfDead();
      }
    }, BOOTSTRAP_SETTLE_MS);
  });

  // 4. Backstop: nothing errored, the bootstrap looked fine, React still never
  //    mounted (e.g. a static import that hangs rather than fails, which also
  //    stalls DOMContentLoaded so the check above never runs).
  window.addEventListener("load", function () {
    window.setTimeout(function () {
      recoverIfDead();
    }, MOUNT_TIMEOUT_MS);
  });

  // Called once React renders. Clears the one-shot guard so a later failure
  // gets its own retry, and tears down the failure overlay if we put one up
  // before the app finally came good.
  window.__palateBootOk = function () {
    window.__palateReady = true;
    try { sessionStorage.removeItem(RETRY_KEY); } catch (e) {}
    try {
      var overlay = document.getElementById("palate-boot-failure");
      if (overlay && overlay.parentNode) overlay.parentNode.removeChild(overlay);
    } catch (e) {}
  };
})();
`;
